import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProductHeader from './components/ProductHeader';
import CurrentCalculations from './components/CurrentCalculations';
import TaxRateMatrix from './components/TaxRateMatrix';
import HistoricalTimeline from './components/HistoricalTimeline';
import RegulatoryBasis from './components/RegulatoryBasis';
import { ProductTaxDetail, TabType, TaxRate, RegulatoryChange } from './types';

const ProductTaxDetailPanel = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('id');

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('current');
  const [selectedRate, setSelectedRate] = useState<TaxRate | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);

  const mockProduct: ProductTaxDetail = {
    id: productId || '1',
    code: 'PROD-2024-001',
    name: 'Notebook Dell Inspiron 15 3000',
    description: 'Notebook para uso corporativo com processador Intel Core i5, 8GB RAM, SSD 256GB, tela 15.6 polegadas Full HD',
    ncm: '8471.30.12',
    cest: '21.001.00',
    category: 'Equipamentos de Informática',
    currentTaxRates: [
      {
        uf: 'SP',
        cfop: '5102',
        cst: '00',
        rate: 18.0,
        effectiveDate: '2024-01-01',
        specialNotes: 'Alíquota padrão para operações internas'
      },
      {
        uf: 'SP',
        cfop: '6102',
        cst: '00',
        rate: 12.0,
        effectiveDate: '2024-01-01',
        specialNotes: 'Alíquota interestadual para estados do Sul e Sudeste'
      },
      {
        uf: 'RJ',
        cfop: '5102',
        cst: '00',
        rate: 20.0,
        effectiveDate: '2024-01-01'
      },
      {
        uf: 'RJ',
        cfop: '6102',
        cst: '00',
        rate: 12.0,
        effectiveDate: '2024-01-01'
      },
      {
        uf: 'MG',
        cfop: '5102',
        cst: '00',
        rate: 18.0,
        effectiveDate: '2024-01-01'
      },
      {
        uf: 'MG',
        cfop: '6102',
        cst: '00',
        rate: 12.0,
        effectiveDate: '2024-01-01'
      },
      {
        uf: 'RS',
        cfop: '5102',
        cst: '00',
        rate: 17.0,
        effectiveDate: '2024-01-01'
      },
      {
        uf: 'BA',
        cfop: '5102',
        cst: '00',
        rate: 19.0,
        effectiveDate: '2024-01-01',
        exemptionConditions: ['Produtos destinados à Zona Franca de Manaus'],
        specialNotes: 'Isenção aplicável mediante apresentação de documentação específica'
      },
      {
        uf: 'PR',
        cfop: '5102',
        cst: '00',
        rate: 18.0,
        effectiveDate: '2024-01-01'
      },
      {
        uf: 'SC',
        cfop: '5102',
        cst: '00',
        rate: 17.0,
        effectiveDate: '2024-01-01'
      }
    ],
    historicalChanges: [
      {
        id: '1',
        date: '2024-01-15',
        type: 'rate_change',
        description: 'Alteração da alíquota de ICMS para operações internas no estado de São Paulo',
        previousValue: '17.0%',
        newValue: '18.0%',
        impactLevel: 'high',
        regulatoryBasis: 'Decreto Estadual nº 67.123/2024 - Publicado no DOE-SP em 10/01/2024'
      },
      {
        id: '2',
        date: '2023-11-20',
        type: 'exemption_added',
        description: 'Inclusão de isenção para produtos destinados à Zona Franca de Manaus no estado da Bahia',
        newValue: 'Isenção Total',
        impactLevel: 'medium',
        regulatoryBasis: 'Convênio ICMS 190/2023 - CONFAZ'
      },
      {
        id: '3',
        date: '2023-09-05',
        type: 'regulation_updated',
        description: 'Atualização das regras de substituição tributária para equipamentos de informática',
        newValue: 'Nova metodologia de cálculo',
        impactLevel: 'high',
        regulatoryBasis: 'Protocolo ICMS 41/2023'
      },
      {
        id: '4',
        date: '2023-06-12',
        type: 'classification_changed',
        description: 'Reclassificação do CEST para notebooks corporativos',
        previousValue: '21.001.01',
        newValue: '21.001.00',
        impactLevel: 'low',
        regulatoryBasis: 'Ajuste SINIEF 15/2023'
      },
      {
        id: '5',
        date: '2023-03-28',
        type: 'rate_change',
        description: 'Redução temporária de alíquota para equipamentos de informática no Rio de Janeiro',
        previousValue: '22.0%',
        newValue: '20.0%',
        impactLevel: 'medium',
        regulatoryBasis: 'Lei Estadual nº 9.876/2023 - RJ'
      }
    ],
    regulatoryBasis: [
      'Lei Complementar nº 87/1996 (Lei Kandir) - Dispõe sobre o imposto dos Estados e do Distrito Federal sobre operações relativas à circulação de mercadorias e sobre prestações de serviços de transporte interestadual e intermunicipal e de comunicação',
      'Convênio ICMS 52/2017 - Dispõe sobre as normas gerais a serem aplicadas aos regimes de substituição tributária e de antecipação de recolhimento do ICMS com encerramento de tributação, relativos às operações subsequentes',
      'Protocolo ICMS 41/2008 - Dispõe sobre a substituição tributária nas operações com equipamentos e componentes para uso em informática e automação',
      'Ajuste SINIEF 19/2012 - Institui o Código Especificador da Substituição Tributária - CEST',
      'Resolução CAMEX nº 125/2016 - Altera a Nomenclatura Comum do Mercosul (NCM) para equipamentos de processamento de dados'
    ],
    complianceNotes: [
      'Atenção: Produtos destinados à Zona Franca de Manaus podem ter tratamento tributário diferenciado. Verificar documentação específica antes do faturamento.',
      'Importante: A classificação NCM 8471.30.12 se aplica especificamente a máquinas automáticas para processamento de dados, portáteis, de peso inferior ou igual a 10 kg.',
      'Observação: Para operações interestaduais, verificar a aplicação da alíquota de 4% conforme Resolução do Senado Federal nº 13/2012 para produtos importados.',
      'Alerta: Alguns estados podem exigir o recolhimento antecipado do ICMS-ST. Consultar legislação específica do estado de destino.',
      'Nota Técnica: A base de cálculo para substituição tributária deve considerar o IVA-ST ajustado conforme Convênio ICMS 52/2017.'
    ],
    lastUpdated: '2024-01-15T10:30:00Z',
    dataSource: 'api',
    apiResponseMetadata: {
      requestId: 'REQ-2024-001-ABC123',
      timestamp: '2024-01-15T10:30:00Z',
      version: '3.0.1'
    }
  };

  useEffect(() => {
    if (!productId) {
      navigate('/consultation-history');
    }
  }, [productId, navigate]);

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportConfirm = (format: 'pdf' | 'excel' | 'csv') => {
    console.log(`Exportando em formato ${format}...`);
    setShowExportModal(false);
  };

  const handleRateClick = (rate: TaxRate) => {
    setSelectedRate(rate);
  };

  const handleChangeClick = (change: RegulatoryChange) => {
    console.log('Detalhes da alteração:', change);
  };

  const handleUserMenuClick = () => {
    console.log('User menu clicked');
  };

  const tabs = [
    { id: 'current' as TabType, label: 'Cálculos Atuais', icon: 'Calculator' },
    { id: 'historical' as TabType, label: 'Histórico', icon: 'Clock' },
    { id: 'regulatory' as TabType, label: 'Base Regulatória', icon: 'Scale' },
    { id: 'compliance' as TabType, label: 'Conformidade', icon: 'CheckCircle2' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onCollapsedChange={setSidebarCollapsed} />
      
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <Header title="Detalhes do Produto" onUserMenuClick={handleUserMenuClick} />
        
        <main className="pt-16 min-h-screen">
          <div className="max-w-7xl mx-auto p-6">
            <div className="mb-6">
              <button
                onClick={() => navigate('/consultation-history')}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="ArrowLeft" size={16} />
                Voltar para Histórico
              </button>
            </div>

            <div className="bg-card rounded-lg shadow-card overflow-hidden">
              <ProductHeader
                product={mockProduct}
                onExport={handleExport}
                onClose={() => navigate('/consultation-history')}
              />

              <div className="border-b border-border">
                <nav className="flex px-6 overflow-x-auto" role="tablist">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all duration-150 whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                      }`}
                    >
                      <Icon name={tab.icon} size={16} color="currentColor" />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {activeTab === 'current' && (
                  <div className="space-y-6">
                    <CurrentCalculations taxRates={mockProduct.currentTaxRates} />
                    <TaxRateMatrix
                      taxRates={mockProduct.currentTaxRates}
                      onRateClick={handleRateClick}
                    />
                  </div>
                )}

                {activeTab === 'historical' && (
                  <HistoricalTimeline
                    changes={mockProduct.historicalChanges}
                    onChangeClick={handleChangeClick}
                  />
                )}

                {activeTab === 'regulatory' && (
                  <RegulatoryBasis
                    regulations={mockProduct.regulatoryBasis}
                    complianceNotes={mockProduct.complianceNotes}
                  />
                )}

                {activeTab === 'compliance' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-foreground">Notas de Conformidade</h3>
                    <div className="space-y-3">
                      {mockProduct.complianceNotes.map((note, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 p-4 bg-accent/5 border border-accent/20 rounded-lg"
                        >
                          <Icon
                            name="AlertCircle"
                            size={18}
                            color="var(--color-accent)"
                            className="mt-0.5 flex-shrink-0"
                          />
                          <p className="text-sm text-foreground leading-relaxed">{note}</p>
                        </div>
                      ))}
                    </div>

                    {mockProduct.apiResponseMetadata && (
                      <div className="mt-6 p-4 bg-muted rounded-lg">
                        <h4 className="text-sm font-semibold text-foreground mb-3">Metadados da API</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Request ID:</span>
                            <p className="font-mono text-foreground mt-1">
                              {mockProduct.apiResponseMetadata.requestId}
                            </p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Timestamp:</span>
                            <p className="font-mono text-foreground mt-1">
                              {new Date(mockProduct.apiResponseMetadata.timestamp).toLocaleString('pt-BR')}
                            </p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Versão API:</span>
                            <p className="font-mono text-foreground mt-1">
                              {mockProduct.apiResponseMetadata.version}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {selectedRate && (
              <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50">
                <div className="bg-card rounded-lg shadow-modal max-w-md w-full p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Detalhes da Alíquota</h3>
                    <button
                      onClick={() => setSelectedRate(null)}
                      className="p-2 hover:bg-muted rounded-md transition-colors"
                    >
                      <Icon name="X" size={20} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-muted-foreground">UF:</span>
                      <p className="font-semibold text-foreground">{selectedRate.uf}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">CFOP:</span>
                      <p className="font-mono text-foreground">{selectedRate.cfop}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">CST:</span>
                      <p className="font-mono text-foreground">{selectedRate.cst}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Alíquota:</span>
                      <p className="text-2xl font-bold text-primary font-mono">
                        {selectedRate.rate.toFixed(2)}%
                      </p>
                    </div>
                    {selectedRate.specialNotes && (
                      <div className="p-3 bg-accent/5 border border-accent/20 rounded-md">
                        <p className="text-sm text-foreground">{selectedRate.specialNotes}</p>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => setSelectedRate(null)}
                    className="mt-4"
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            )}

            {showExportModal && (
              <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50">
                <div className="bg-card rounded-lg shadow-modal max-w-md w-full p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Exportar Detalhes</h3>
                    <button
                      onClick={() => setShowExportModal(false)}
                      className="p-2 hover:bg-muted rounded-md transition-colors"
                    >
                      <Icon name="X" size={20} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Selecione o formato de exportação desejado:
                  </p>
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="FileText"
                      iconPosition="left"
                      onClick={() => handleExportConfirm('pdf')}
                    >
                      Exportar como PDF
                    </Button>
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="FileSpreadsheet"
                      iconPosition="left"
                      onClick={() => handleExportConfirm('excel')}
                    >
                      Exportar como Excel
                    </Button>
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="FileDown"
                      iconPosition="left"
                      onClick={() => handleExportConfirm('csv')}
                    >
                      Exportar como CSV
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProductTaxDetailPanel;